import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from '../../../../store/redux/hooks';
import {
  addFieldTranslations,
  removeFieldTranslations,
} from '../../../../store/redux/slices/pass.slice';
import type { FieldLocalisations } from '../../../../store/redux/slices/pass.slice';
import { getTemplateFieldSlots, hasLocaleContent } from '../utils/passEditState';
import type { PassTemplateMeta } from '../../../../store/types/pass';

export const usePassLocalisation = (
  template: PassTemplateMeta | undefined,
  templateFields: Record<string, Record<string, { localisation?: Record<string, unknown> } | null>> | undefined,
  localisationEnabled: boolean,
  fieldLocalisations: FieldLocalisations,
  errors: Record<string, string>
) => {
  const dispatch = useAppDispatch();

  const fieldSlots = useMemo(
    () => (template ? getTemplateFieldSlots(template) : []),
    [template]
  );

  const hasLocalisation = useMemo(() => {
    if (!localisationEnabled) return false;
    if (!templateFields) return false;
    if (fieldSlots.length === 0) return false;
    return fieldSlots.some(([section, slot, fieldDef]) => {
      if (!fieldDef?.id) return false;
      const templateField = templateFields[section]?.[slot];
      if (!templateField?.localisation) return false;
      return Object.values(templateField.localisation).some(hasLocaleContent);
    });
  }, [localisationEnabled, templateFields, fieldSlots]);

  // All fields should show translation inputs when the template has any languages
  const fieldsWithLocalisation = useMemo(() => {
    if (!hasLocalisation) return new Set<string>();
    const set = new Set<string>();
    fieldSlots.forEach(([, , fieldDef]) => {
      if (fieldDef?.id) set.add(fieldDef.id);
    });
    return set;
  }, [hasLocalisation, fieldSlots]);

  const templateLanguages = useMemo(() => {
    if (!templateFields) return [];
    const langs = new Set<string>();
    Object.values(templateFields).forEach((section) => {
      if (!section) return;
      Object.values(section).forEach((field) => {
        if (field?.localisation) {
          Object.entries(field.localisation).forEach(([locale, loc]) => {
            if (hasLocaleContent(loc)) langs.add(locale);
          });
        }
      });
    });
    return Array.from(langs);
  }, [templateFields]);


  const [expandedTranslations, setExpandedTranslations] = useState<Record<string, boolean>>({});

  // Auto-expand translations for fields with localisation errors
  useEffect(() => {
    const fieldsWithErrors = new Set<string>();
    Object.keys(errors).forEach((path) => {
      if (path.startsWith('localisation.')) {
        fieldsWithErrors.add(path.split('.')[1]);
      }
    });
    if (fieldsWithErrors.size === 0) return;

    setExpandedTranslations((prev) => {
      const next = { ...prev };
      fieldsWithErrors.forEach((fieldId) => { next[fieldId] = true; });
      return next;
    });
    fieldsWithErrors.forEach((fieldId) => {
      if (!fieldLocalisations[fieldId]) {
        dispatch(addFieldTranslations({ fieldId, locales: templateLanguages }));
      }
    });
  }, [errors]);

  const handleAddTranslations = (fieldId: string) => {
    dispatch(addFieldTranslations({ fieldId, locales: templateLanguages }));
    setExpandedTranslations((prev) => ({ ...prev, [fieldId]: true }));
  };

  const handleRemoveTranslations = (fieldId: string) => {
    dispatch(removeFieldTranslations(fieldId));
    setExpandedTranslations((prev) => ({ ...prev, [fieldId]: false }));
  };

  return {
    fieldSlots,
    hasLocalisation,
    fieldsWithLocalisation,
    templateLanguages,
    expandedTranslations,
    handleAddTranslations,
    handleRemoveTranslations,
  };
};
